import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  Avatar, 
  Divider, 
  List, 
  ListItem, 
  ListItemText, 
  Chip,
  Button,
  IconButton,
  Tooltip,
  Tab,
  Tabs
} from '@mui/material';
import { 
  Edit as EditIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  PhotoCamera as PhotoCameraIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  AdminPanelSettings as AdminPanelSettingsIcon // Ajout de l'icône pour le bouton d'administration
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { CategoryProvider } from '../context/CategoryContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import EditProfileDialog from '../components/profile/EditProfileDialog';
import ProfileImageUpload from '../components/profile/ProfileImageUpload';
import UserPreferences from '../components/profile/UserPreferences';
import UserCategories from '../components/profile/UserCategories';
import StripeAccountStatusCard from '../components/financial/StripeAccountStatusCard';
import api from '../services/api';
import { Link as RouterLink } from 'react-router-dom';
import { getTokenAuth } from '../utils/authUtils';
import { useTranslation } from 'react-i18next';

// Composant TabPanel pour gérer les onglets
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ProfilePage = () => {
  const { user, refreshUserData } = useAuth();
  const { formatPrice } = useCurrency();
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openImageUploadDialog, setOpenImageUploadDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [userListings, setUserListings] = useState([]);
  const { t } = useTranslation();
  // Vérifier si l'utilisateur est admin
  const isAdmin = user?.isAdmin || false;
  
  // Effet pour rafraîchir les données utilisateur au chargement de la page
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        console.log('ProfilePage: Rafraîchissement des données utilisateur...');
        
        // Vérifier si l'utilisateur est connect��
        const token = getTokenAuth();
        if (!token) {
          console.log('Aucun token trouvé, impossible de rafraîchir les données');
          return;
        }

        // Vérifier si les données utilisateur sont déjà chargées
        const storedUserData = localStorage.getItem('userData');
        if (storedUserData) {
          const userData = JSON.parse(storedUserData);
          // Si les données essentielles sont déjà présentes, ne pas rafraîchir
          if (userData && userData.profilePicture && userData.preferences) {
            console.log('Données utilisateur déjà complètes, pas besoin de rafraîchir');
            return;
          }
        }
        
        // Configurer le token pour la requête
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Appeler l'API pour obtenir les données utilisateur à jour
        const response = await api.get('/auth/me');
        const freshUserData = response.data.user;
        
        console.log('Données utilisateur récupérées:', freshUserData);
        console.log('Photo de profil présente:', !!freshUserData.profilePicture);
        console.log('Préférences présentes:', !!freshUserData.preferences);
        
        // Mettre à jour le localStorage
        localStorage.setItem('userData', JSON.stringify(freshUserData));
        
        // Si refreshUserData existe, l'utiliser pour mettre à jour l'état global
        if (refreshUserData) {
          refreshUserData();
        }
      } catch (error) {
        console.error('Erreur lors du rafraîchissement des données utilisateur:', error);
      }
    };
    
    fetchUserData();
  }, []); // Exécuter uniquement au montage du composant
  
  // Effet pour charger les annonces de l'utilisateur
  useEffect(() => {
    const fetchUserListings = async () => {
      if (!user || !user.id) return;
      
      try {
        const response = await api.get(`/listings/user/${user.id}`);
        setUserListings(response.data.listings || []);
      } catch (error) {
        console.error('Erreur lors du chargement des annonces de l\'utilisateur:', error);
        setUserListings([]);
      }
    };
    
    fetchUserListings();
  }, [user]);
  
  // Formater la date d'inscription (exemple)
  const formatDate = (dateString) => {
    if (!dateString) return 'Non disponible';
    try {
      return format(new Date(dateString), 'dd MMMM yyyy', { locale: fr });
    } catch (error) {
      return 'Date invalide';
    }
  };

  const handleOpenEditDialog = () => {
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    // Rafraîchir les données après la fermeture du dialogue
    if (refreshUserData) {
      refreshUserData();
    }
  };

  const handleOpenImageUploadDialog = () => {
    setOpenImageUploadDialog(true);
  };

  const handleCloseImageUploadDialog = () => {
    setOpenImageUploadDialog(false);
    // Rafraîchir les données après la fermeture du dialogue
    if (refreshUserData) {
      refreshUserData();
    }
  };
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Fonction pour rafraîchir manuellement les données
  const handleManualRefresh = async () => {
    if (isRefreshing || !refreshUserData) return;
    
    setIsRefreshing(true);
    try {
      await refreshUserData();
    } catch (error) {
      console.error('Erreur lors du rafraîchissement manuel:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
              <Box sx={{ py: 4 }}>
            <Container maxWidth="lg">
              <Box className="payment-history-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4" component="h1">
                  {t('profile.title')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {/* Bouton d'administration - visible uniquement pour les admins */}
                  {isAdmin && (
                    <Button
                      variant="contained"                      
                      color="primary"
                      startIcon={<AdminPanelSettingsIcon />}
                      component={RouterLink}
                      to="/admin"
                      sx={{
                        background: theme => `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                        boxShadow: '0 3px 5px 2px rgba(33, 150, 243, .3)',
                        border: '1px solid white'
                      }}
                    >
                      {t('profile.administration')}
                    </Button>
                  )}
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={handleManualRefresh}
                    disabled={isRefreshing}
                    variant="outlined"
                    size="small"
                    sx={{ border: '1px solid white', color: 'white', backgroundColor: '#141fbd', '&:hover': { backgroundColor: '#1565c0' } }}
                  >
                    {isRefreshing ? t('profile.refreshing') : t('profile.refresh')}
                  </Button>
                </Box>
              </Box>
              <Grid container spacing={3}>
                {/* Section principale du profil */}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                      <Avatar
                        src={user?.profilePicture ? `${user.profilePicture}?${new Date().getTime()}` : undefined}
                        alt={user?.name}
                        sx={{
                          width: 120,
                          height: 120,
                          mx: 'auto',
                          mb: 2,
                          bgcolor: 'primary.main'
                        }}
                      >
                        {user?.name?.charAt(0) || t('profile.user')}
                      </Avatar>
                      <Tooltip title={t('profile.changePhoto')}>
                        <IconButton
                          sx={{
                            position: 'absolute',
                            bottom: 10,
                            right: -10,
                            bgcolor: 'background.paper',
                            boxShadow: 1,
                            '&:hover': {
                              bgcolor: 'background.paper',
                            }
                          }}
                          onClick={handleOpenImageUploadDialog}
                        >
                          <PhotoCameraIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="h5" gutterBottom>
                      {user?.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {user?.email}
                    </Typography>
                    <Box sx={{ mt: 2, mb: 3 }}>
                      {user?.isTenant && (
                        <Chip
                          label={t('profile.tenant')}
                          color="primary"
                          size="small"
                          sx={{ mr: 1 }}
                        />
                      )}
                      {user?.isOwner && (
                        <Chip
                          label={t('profile.owner')}
                          color="secondary"
                          size="small"
                          sx={{ mr: 1 }}
                        />
                      )}
                      {user?.isAdmin && (
                        <Chip
                          label={t('profile.admin')}
                          color="error"
                          size="small"
                        />
                      )}
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      fullWidth
                      onClick={handleOpenEditDialog}
                      sx={{ mb: 2 }}
                    >
                      {t('profile.editProfile')}
                    </Button>
                    {/* Bouton d'administration dans la section profil - visible uniquement pour les admins */}
                    {isAdmin && (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AdminPanelSettingsIcon />}
                        fullWidth
                        component={RouterLink}
                        to="/admin"
                        sx={{
                          background: theme => `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                        }}
                      >
                        {t('profile.accessAdministration')}
                      </Button>
                    )}
                  </Paper>
                </Grid>
                {/* Informations détaillées et préférences */}
                <Grid item xs={12} md={8}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                      value={tabValue}
                      onChange={handleTabChange}
                      aria-label="profile tabs"
                    >
                      <Tab
                        icon={<PersonIcon />}
                        iconPosition="start"
                        label={t('profile.information')}
                        id="profile-tab-0"
                        aria-controls="profile-tabpanel-0"
                      />
                      <Tab
                        icon={<SettingsIcon />}
                        iconPosition="start"
                        label={t('profile.preferences')}
                        id="profile-tab-1"
                        aria-controls="profile-tabpanel-1"
                      />
                    </Tabs>
                  </Box>
                  <TabPanel value={tabValue} index={0}>
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        {t('profile.personalInformation')}
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <List>
                        <ListItem>
                          <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
                          <ListItemText
                            primary={t('profile.fullName')}
                            secondary={user?.name || t('profile.notProvided')}
                          />
                        </ListItem>
                        <ListItem>
                          <EmailIcon sx={{ mr: 2, color: 'primary.main' }} />
                          <ListItemText
                            primary={t('profile.email')}
                            secondary={user?.email || t('profile.notProvided')}
                          />
                        </ListItem>
                        <ListItem>
                          <PhoneIcon sx={{ mr: 2, color: 'primary.main' }} />
                          <ListItemText
                            primary={t('profile.phone')}
                            secondary={user?.phone || t('profile.notProvided')}
                          />
                        </ListItem>
                        <ListItem>
                          <CalendarIcon sx={{ mr: 2, color: 'primary.main' }} />
                          <ListItemText
                            primary={t('profile.registrationDate')}
                            secondary={formatDate(user?.createdAt) || t('profile.notAvailable')}
                          />
                        </ListItem>
                      </List>
                    </Paper>
                    {/* Informations spécifiques au rôle */}
                    {user?.isTenant && (
                      <Paper sx={{ p: 3, mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          {t('profile.tenantProfile')}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <List>
                          <ListItem>
                            <ListItemText
                              primary={t('profile.profession')}
                              secondary={user?.tenantProfile?.occupation || t('profile.notSpecified')}
                            />
                          </ListItem>
                         
                          <ListItem>
                            <ListItemText
                              primary={t('profile.preferredLocation')}
                              secondary={user?.tenantProfile?.preferredLocation || t('profile.notSpecified')}
                            />
                          </ListItem>
                        </List>
                      </Paper>
                    )}
                    {user?.isOwner && (
                      <Paper sx={{ p: 3, mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          {t('profile.ownerProfile')}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <List>
                          <ListItem>
                            <ListItemText
                              primary={t('profile.companyName')}
                              secondary={user?.ownerProfile?.companyName || t('profile.notProvided')}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText
                              primary={t('profile.taxNumber')}
                              secondary={user?.ownerProfile?.taxIdentificationNumber || t('profile.notProvided')}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText
                              primary={t('profile.yearsExperience')}
                              secondary={user?.ownerProfile?.yearsOfExperience ? `${user.ownerProfile.yearsOfExperience} ans` : t('profile.notProvided')}
                            />
                          </ListItem>
                        </List>
                        <CategoryProvider>
                          <UserCategories userListings={userListings} />
                        </CategoryProvider>
                      </Paper>
                    )}
                    {user?.isOwner && (
                      <Box sx={{ mt: 3 }}>
                        <StripeAccountStatusCard user={user} variant="card" />
                      </Box>
                    )}
                  </TabPanel>
                  <TabPanel value={tabValue} index={1}>
                    <UserPreferences />
                  </TabPanel>
                </Grid>
              </Grid>
              {/* Dialogues */}
              <EditProfileDialog
                open={openEditDialog}
                onClose={handleCloseEditDialog}
              />
              <ProfileImageUpload
                open={openImageUploadDialog}
                onClose={handleCloseImageUploadDialog}
              />
            </Container>
          </Box>
  );
};

export default ProfilePage;